import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/auth/Login';
import Dashboard from './components/dashboard/Dashboard';
import Properties from './components/properties/Properties';
import Announcements from './components/announcements/Announcements';
import Documents from './components/documents/Documents';
import FinancialOverview from './components/financial/FinancialOverview';
import Reports from './components/reports/Reports';
import AdminDashboard from './components/admin/AdminDashboard';
import UserManagement from './components/admin/UserManagement';
import BuildingManagement from './components/admin/BuildingManagement';
import PropertyManagement from './components/admin/PropertyManagement';
import AnnouncementManagement from './components/admin/AnnouncementManagement';
import DocumentManagement from './components/admin/DocumentManagement';
import FinancialManagement from './components/admin/FinancialManagement';
import Layout from './components/layout/Layout';
import './index.css';

// Componente principal que maneja la lógica de autenticación
function AppContent() {
  const { isAuthenticated, loading, user } = useAuth();
  
  // Determinar la vista inicial basada en el tipo de usuario
  const getInitialView = () => {
    if (user?.tipoSocio === 'administrador') {
      return 'admin';
    }
    return 'dashboard';
  };
  
  const [currentView, setCurrentView] = useState<'dashboard' | 'properties' | 'announcements' | 'documents' | 'financial' | 'reports' | 'admin' | 'admin-users' | 'admin-buildings' | 'admin-properties' | 'admin-announcements' | 'admin-documents' | 'admin-financial'>(getInitialView());

  // Actualizar la vista cuando el usuario se carga
  useEffect(() => {
    if (user && !loading) {
      const initialView = getInitialView();
      setCurrentView(initialView);
    }
  }, [user, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-hotel-50 to-hotel-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hotel-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Login />;
  }

  // Función para renderizar el contenido según la vista actual
  const renderContent = () => {
    switch (currentView) {
      case 'dashboard':
        return <Dashboard />;
      case 'properties':
        return <Properties />;
      case 'announcements':
        return <Announcements />;
      case 'documents':
        return <Documents />;
      case 'financial':
        return <FinancialOverview />;
      case 'reports':
        return <Reports />;
      case 'admin':
        return <AdminDashboard />;
      case 'admin-users':
        return <UserManagement />;
      case 'admin-buildings':
        return <BuildingManagement />;
      case 'admin-properties':
        return <PropertyManagement />;
      case 'admin-announcements':
        return <AnnouncementManagement />;
      case 'admin-documents':
        return <DocumentManagement />;
      case 'admin-financial':
        return <FinancialManagement />;
      default:
        return <Dashboard />;
    }
  };

  return (
    <Layout currentView={currentView} onViewChange={setCurrentView}>
      {renderContent()}
    </Layout>
  );
}

// Componente principal envuelto en el provider de autenticación
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
